"use client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRightIcon,
  CheckIcon,
  MinusIcon,
  PlusIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
} from "lucide-react";
import { Fragment, useState } from "react";
import { AVAILABILITY } from "./availability-display";
import { api } from "@/trpc/react";
import { toast } from "sonner";
import Link from "next/link";
import { nanoid } from "nanoid";
import { ShowInstallments } from "../../../components/show-installments";

export const PutToCartButton = ({
  variantId,
  availability = AVAILABILITY.AVAILABLE,
  isDefaultVariant = false,
  serviceAddons = [],
  currentPrice,
  onClick,
}: {
  variantId?: string;
  availability?: AVAILABILITY;
  isDefaultVariant?: boolean;
  serviceAddons?: {
    id: string;
  }[];
  currentPrice?: number;
  onClick?: () => void;
}) => {
  const { mutateAsync: addToCart, isPending } =
    api.shoppingCart.addItemToCart.useMutation();
  const apiUtils = api.useUtils();
  const [units, setUnits] = useState(1);
  const unitInput = units <= 0 ? "" : `${units}`;

  const exists = !!variantId;
  const available = availability === "AVAILABLE" && units > 0 && exists;

  const totalPrice =
    currentPrice && currentPrice > 0 ? currentPrice * units : undefined;

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full flex-row gap-2">
        <div className="group flex w-full flex-row gap-0 rounded-xl border-2 bg-white py-0.5 focus-within:border-primary-400">
          <Button
            variant="ghost"
            className="gro-0"
            onClick={() => setUnits(Math.max(1, units - 1))}
          >
            <MinusIcon className="h-4 w-4" />
          </Button>
          <Input
            type="number"
            inputMode="numeric"
            value={unitInput}
            className="without-ring w-14 grow border-none text-center md:w-32"
            onChange={(e) => {
              const num = parseInt(e.target.value, 10);
              if (isNaN(num)) {
                setUnits(0);
              } else {
                setUnits(Math.max(0, num));
              }
            }}
          />
          <Button
            className="grow-0"
            variant="ghost"
            onClick={() => setUnits(units + 1)}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Button
        className="w-full"
        variant={"default"}
        type="button"
        left={<ShoppingCartIcon className="h-4 w-4" />}
        disabled={!available}
        isLoading={isPending}
        onClick={async (e) => {
          e.preventDefault();
          if (available) {
            const done = await addToCart({
              id: variantId!,
              quantity: units,
              services: serviceAddons.map((s) => s.id),
            });

            if (done) {
              setUnits(1);
              const u = nanoid(4);

              toast.success(
                <div className="flex w-full flex-row justify-between gap-4">
                  <div className="h-8 w-8 shrink-0 self-center pl-2">
                    <CheckIcon className="h-8 w-8 text-green-700" />
                  </div>
                  <div className="flex w-full grow flex-col items-end gap-1">
                    <div className="font-bold">
                      Produkt in den Warenkorb gelegt
                    </div>
                    <Link href={`/shopping-cart`}>
                      <Button
                        variant="outline"
                        size="sm"
                        right={<ArrowRightIcon size={12} />}
                      >
                        Warenkorb ansehen
                      </Button>
                    </Link>
                  </div>
                </div>,
                {
                  position: "top-right",
                },
              );
            } else {
              toast.error(
                "Produkt konnte nicht in den Warenkorb gelegt werden",
              );
            }

            apiUtils.shoppingCart.getCart.invalidate();

            onClick?.();
          }
        }}
      >
        {availability === "AVAILABLE" &&
          (units > 0 ? "In Warenkorb" : "Wähle mind. 1 Stück aus")}
        {availability === "NEEDS_SELECTION" && "Wähle eine Konfiguration aus"}
        {availability === "NOT_AVAILABLE" && "Nicht verfügbar"}
      </Button>

      {!!currentPrice && <ShowInstallments price={currentPrice} />}
    </div>
  );
};

export const PutToCartButtonLoader = () => {
  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full flex-row gap-2">
        <div className="group flex w-full flex-row gap-0 rounded-xl border-2 bg-white py-0.5 focus-within:border-primary-400">
          <Button variant="ghost" className="gro-0">
            <MinusIcon className="h-4 w-4" />
          </Button>
          <Input
            type="number"
            inputMode="numeric"
            value="0"
            className="without-ring w-14 grow border-none text-center md:w-32"
            disabled
          />
          <Button className="grow-0" variant="ghost">
            <PlusIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <Button
        className="w-full"
        variant={"default"}
        type="button"
        left={<ShoppingBagIcon className="h-4 w-4" />}
        disabled
        isLoading
      >
        Wird geladen
      </Button>
    </div>
  );
};
